import { environment } from 'src/environments/environment';
import { renderFullQuality } from 'src/app/shared/feature-flags';
import { RENDER_DRONE_FOLDER, RENDER_OUTPUT_SPEC, RENDER_PRICE_USD, RENDER_SAMPLES, RenderSample, RenderSampleId, renderFaq } from './render.data';

// Content of the landing, /render: the static concept src/render-demo (House Lights, now archived at
// /render-demo-archive) moved into the app (docs/render-v2-landing.md). The copy is the concept's;
// numbers come from render.data.

// --- the renders ---

// Two sets, by `renderFullQuality` (feature-flags.ts):
// - the October renders: the pipeline's own files, HEVC at about 2 Mbps, 1920 × 1038 and 856 × 1852
//   (copied to public storage /_render-hq; the renderer's size until 2026-11-02), and an H.264 copy
//   from /_render-demo for a browser without HEVC (Firefox, some Windows machines);
// - the designer's 1080p renders (2026-11-03): the pipeline's files are H.264 at 5–8 Mbps, 8–12 MB,
//   1920 × 1080 and 1080 × 1920, 60 fps — what a customer gets, in /_render-hq as they came. The page
//   plays copies from /_render-web: HEVC CRF 20 under a 3.5 Mbps cap (about 3 MB, SSIM 0.997 against
//   the file), H.264 CRF 21 for a browser without HEVC, a WebP of the first frame as the poster.
//   `full` is the file itself, for the band's "Full quality" viewer.
export interface RenderReel {
  hevc: string;
  h264: string;
  poster: string;
  full?: RenderFullFile;
}

export interface RenderFullFile {
  url: string;
  bytes: number;
  width: number;
  height: number;
}

// The portrait reel's shape, for the hero's narrowing frame: its files' (856 × 1852 for the October
// renders), not the product's output (RENDER_OUTPUT_SPEC). The page hands it to the `.mp` aspect
// ratio (render-landing-v2.component.scss).
export const RENDER_REEL_PORTRAIT_RATIO = renderFullQuality ? 1080 / 1920 : 856 / 1852;

const HQ = `${environment.landingMediaBaseUrl}/_render-hq`;
const DEMO = `${environment.landingMediaBaseUrl}/_render-demo`;
const WEB = `${environment.landingMediaBaseUrl}/_render-web`;

// `folder` is the render's name in the pipeline; `name` is the H.264 copy's.
function reel(folder: string, orientation: 'web' | 'app', name: string): RenderReel {
  return {
    hevc: `${HQ}/${folder}/${folder}_${orientation}.mp4`,
    h264: `${DEMO}/${name}.mp4`,
    poster: `${DEMO}/${name}.webp`,
  };
}

// A 1080p render: the page's copies and the file as delivered (`bytes`, its size, for the label).
function film(folder: string, orientation: 'web' | 'app', bytes: number): RenderReel {
  const name = `${folder}_${orientation}`;
  return {
    hevc: `${WEB}/${folder}/${name}.hevc.mp4`,
    h264: `${WEB}/${folder}/${name}.h264.mp4`,
    poster: `${WEB}/${folder}/${name}.webp`,
    full: { url: `${HQ}/${folder}/${name}.mp4`, bytes, width: orientation === 'web' ? 1920 : 1080, height: orientation === 'web' ? 1080 : 1920 },
  };
}

// The designer's renders of 2026-11-03 (spaces in the drone's name made underscores on storage).
const SAMURAI_1080 = 'Cyber_Samurai1793608991183';
const DRONE_1080 = 'Sci-Fi_Vehicle_Series-Drone_11793623753912';
const CAR_1080 = 'lamba_car1793609138452';

function sample(id: RenderSampleId): RenderSample {
  return RENDER_SAMPLES.find((item) => item.id === id) as RenderSample;
}

// One frame, two compositions: the hero's 16:9 frame narrows to the portrait on the same second.
export const RENDER_V2_HERO = renderFullQuality
  ? { sample: sample('samurai'), landscape: film(SAMURAI_1080, 'web', 8033308), portrait: film(SAMURAI_1080, 'app', 12339007) }
  : { sample: sample('samurai'), landscape: reel('Cyber_Samurai1760419016510', 'web', 'samurai'), portrait: reel('Cyber_Samurai1760419016510', 'app', 'samurai-p') };

export interface RenderBand {
  sample: RenderSample;
  reel: RenderReel;
  // The same render's portrait film, for a phone (designer, 2026-10-13).
  portrait: RenderReel;
}

// Wide, compact, tall: each render across the full window, the room dims while it plays.
export const RENDER_V2_BANDS: ReadonlyArray<RenderBand> = renderFullQuality
  ? [
    { sample: sample('drone'), reel: film(DRONE_1080, 'web', 8505539), portrait: film(DRONE_1080, 'app', 8075388) },
    { sample: sample('car'), reel: film(CAR_1080, 'web', 8986325), portrait: film(CAR_1080, 'app', 8410881) },
    { sample: sample('samurai'), reel: film(SAMURAI_1080, 'web', 8033308), portrait: film(SAMURAI_1080, 'app', 12339007) },
  ]
  : [
    // The drone in place of the revolver (designer, 2026-10-16).
    { sample: sample('drone'), reel: reel(RENDER_DRONE_FOLDER, 'web', 'drone'), portrait: reel(RENDER_DRONE_FOLDER, 'app', 'drone-p') },
    { sample: sample('car'), reel: reel('lamba_car1760418509673', 'web', 'car'), portrait: reel('lamba_car1760418509673', 'app', 'car-p') },
    { sample: sample('samurai'), reel: reel('Cyber_Samurai1760419016510', 'web', 'samurai'), portrait: reel('Cyber_Samurai1760419016510', 'app', 'samurai-p') },
  ];

// --- the page's copy ---

export const RENDER_V2_STEPS: ReadonlyArray<{ title: string; text: string }> = [
  { title: 'Bring your model', text: 'FBX or USDZ, with textures packed in or in the same folder.' },
  { title: 'See it in our light', text: 'One frame in both compositions, before you pay.' },
  { title: 'Take the film', text: `${RENDER_OUTPUT_SPEC.seconds} s at ${RENDER_OUTPUT_SPEC.fps} fps, both formats, platform versions included.` },
];

export interface RenderNote {
  question: string;
  answer: string;
  // Ends with "Export guide" (the help dialog's guide tab).
  guide?: boolean;
}

// Written for professionals, then /render's two texture questions in its own words. All six
// open on the page: no "See more" (designer, 2026-10-13).
export const RENDER_V2_NOTES: ReadonlyArray<RenderNote> = [
  // The texture formats belong to the files (designer, 2026-10-13), /render's list.
  {
    question: 'Which files can I send?',
    answer: 'FBX and USDZ. Send a single file, or a folder with the model and its textures: PNG, JPG, TGA, TIFF or EXR, and PNG, TGA or TIFF for transparency. We check the files before anything is rendered.',
    guide: true,
  },
  // Designer, 2026-10-13: one more row, /render's question.
  {
    question: 'Can I see my result before paying?',
    answer: 'Yes. Sign in and see a frame of your model in both compositions first. No payment card is needed for that.',
  },
  // Alex, 2026-11-03: "available for at least 30 days after completion", as the Terms say (was "at any
  // time").
  {
    question: `What does $${RENDER_PRICE_USD} include?`,
    answer: 'Both 360° films of one model, landscape and portrait, and the platform versions. They stay available to download for at least 30 days after completion.',
  },
  {
    question: 'What if the first frame is not right?',
    answer: 'Then you do not buy it. Adjust the model or its materials and send it again.',
  },
  // "Why does my model look white?" and "How do I include my textures?", both ending on the Export
  // guide. ZIP is not mentioned in them, so the browser's ZIP support does not matter here.
  ...renderFaq(false).filter((item) => item.textures && item.guide),
];

// --- Community: Original 3D Artists, our conversations on YouTube ---
// Alex (Slack, 2026-10-09): the section leads to the channel. The Faces rail (owner's pick,
// 2026-10-12; the other variants are in src/render-demo/README.md).

export const RENDER_CHANNEL_URL = 'https://www.youtube.com/@3dwayio';
export const RENDER_CHANNEL_SUBSCRIBE_URL = `${RENDER_CHANNEL_URL}?sub_confirmation=1`;
// The footer's, the same account the render emails link to (designer, 2026-11-04).
export const RENDER_INSTAGRAM_URL = 'https://www.instagram.com/3dway.io/';
// The channel page's count as a floor with "+" (74.2K on the day): it stays true while the channel
// grows, where the exact figure went stale between episodes (designer, 2026-11-02: 69.2K on the page,
// 74.2K on YouTube). Raised with each new episode (render-podcast-episode).
// From the channel page, 2026-11-02.
export const RENDER_CHANNEL_SUBSCRIBERS = '74K+';

export interface RenderEpisode {
  id: string;
  // The cover's title.
  title: string;
  guest: string;
  // The cover's discipline line.
  discipline: string;
  runtime: string;
  // The auto frame (maxres1–3) that shows the call, and the half of its split screen the guest sits in.
  frame: 'maxres1' | 'maxres2' | 'maxres3';
  side: 'l' | 'r';
}

// Newest first. Alex hosts. A new episode: the render-podcast-episode skill (.claude/skills).
export const RENDER_EPISODES: ReadonlyArray<RenderEpisode> = [
  { id: 'RC68A5kOujs', title: 'Make It Read', guest: 'Nicolas De La Torre', discipline: 'Character art', runtime: '3:14:48', frame: 'maxres3', side: 'l' },
  { id: 'ItnHbxQf1pw', title: 'Picture First', guest: 'Arthur Dorety', discipline: '3D art into a fantasy world', runtime: '1:09:05', frame: 'maxres1', side: 'l' },
  { id: '1EudNcmmX1c', title: 'Not Sculpted', guest: 'Francesco Furneri', discipline: 'Substance Designer', runtime: '2:00:07', frame: 'maxres2', side: 'l' },
  { id: 'Agr0ZuSrB80', title: 'Built With Nodes', guest: 'Reyner Sumarriva', discipline: 'Substance Designer', runtime: '1:29:42', frame: 'maxres1', side: 'l' },
  { id: 'd4sbJFfZUtU', title: 'Small Team, Real Game', guest: 'Sarah Malcom', discipline: 'Indie development', runtime: '2:02:51', frame: 'maxres1', side: 'r' },
  { id: '75B2XeN4JNo', title: 'Rebuilding the Past', guest: 'Angel Nieves', discipline: 'Creature art', runtime: '2:09:03', frame: 'maxres2', side: 'r' },
  { id: 'KgXLfT0pbeM', title: 'Light Tells Stories', guest: 'Artur Artinian', discipline: 'Environment art & lighting', runtime: '1:50:04', frame: 'maxres1', side: 'r' },
  { id: '1cB9a1JdBJM', title: 'Not Just Quads', guest: 'Jack Mavar', discipline: 'Game-ready art', runtime: '1:12:50', frame: 'maxres1', side: 'l' },
  { id: 'tLcU61eva90', title: 'Keep Your Voice', guest: 'Kendrick Russell', discipline: 'Environment art', runtime: '1:47:19', frame: 'maxres1', side: 'l' },
  { id: 'gPV4huYtqa4', title: 'Built to Change', guest: 'Sergio Suarez', discipline: 'Material art', runtime: '2:08:50', frame: 'maxres1', side: 'l' },
  { id: 'j5F34lvK8HI', title: 'Made to Repeat', guest: 'Travis Sindewald', discipline: 'Procedural game art', runtime: '1:51:34', frame: 'maxres1', side: 'l' },
  { id: 'tob3RIRDhno', title: 'Built for Production', guest: 'Ondrej Malota', discipline: 'Environment art', runtime: '2:29:09', frame: 'maxres1', side: 'r' },
];

export function renderEpisodeUrl(episode: RenderEpisode): string {
  return `https://www.youtube.com/watch?v=${episode.id}`;
}

export function renderEpisodeFrame(episode: RenderEpisode): string {
  return `https://i.ytimg.com/vi/${episode.id}/${episode.frame}.jpg`;
}
